/**
 * Admonition — Phase 6a.9 per `frontend-specs/design-patterns/01-admonition.md`.
 *
 * Inline alert/notice boxes for displaying contextual messages to auditors.
 * Seven semantic variants:
 *   • note (blue)     — IFRS clause reminders, general notes
 *   • info (cyan)     — informational context
 *   • tip (green)     — helpful suggestions
 *   • success         — calculation completed, identity check holds
 *   • warning (amber) — validation warning, audit period bounds
 *   • caution (orange) — material variance detected
 *   • danger (red)    — calculation error, validation failure
 *
 * Bilingual support: callers pass i18n-translated strings via `title` and
 * children. The component itself contains no copy.
 */

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  Lightbulb,
  XCircle,
} from "lucide-react";
import * as React from "react";

const admonitionConfig = {
  note: {
    bgColor: "bg-blue-50 dark:bg-blue-950/30",
    borderColor: "border-blue-200 dark:border-blue-800",
    textColor: "text-blue-700 dark:text-blue-300",
    iconColor: "text-blue-600 dark:text-blue-400",
    icon: Info,
  },
  tip: {
    bgColor: "bg-green-50 dark:bg-green-950/30",
    borderColor: "border-green-200 dark:border-green-800",
    textColor: "text-green-700 dark:text-green-300",
    iconColor: "text-green-600 dark:text-green-400",
    icon: Lightbulb,
  },
  info: {
    bgColor: "bg-cyan-50 dark:bg-cyan-950/30",
    borderColor: "border-cyan-200 dark:border-cyan-800",
    textColor: "text-cyan-700 dark:text-cyan-300",
    iconColor: "text-cyan-600 dark:text-cyan-400",
    icon: Info,
  },
  warning: {
    bgColor: "bg-amber-50 dark:bg-amber-950/30",
    borderColor: "border-amber-200 dark:border-amber-700",
    textColor: "text-amber-700 dark:text-amber-300",
    iconColor: "text-amber-600 dark:text-amber-400",
    icon: AlertTriangle,
  },
  danger: {
    bgColor: "bg-red-50 dark:bg-red-950/30",
    borderColor: "border-red-200 dark:border-red-800",
    textColor: "text-red-700 dark:text-red-300",
    iconColor: "text-red-600 dark:text-red-400",
    icon: XCircle,
  },
  success: {
    bgColor: "bg-emerald-50 dark:bg-emerald-950/30",
    borderColor: "border-emerald-200 dark:border-emerald-800",
    textColor: "text-emerald-700 dark:text-emerald-300",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    icon: CheckCircle,
  },
  caution: {
    bgColor: "bg-orange-50 dark:bg-orange-950/30",
    borderColor: "border-orange-200 dark:border-orange-700",
    textColor: "text-orange-700 dark:text-orange-300",
    iconColor: "text-orange-600 dark:text-orange-400",
    icon: AlertCircle,
  },
} as const;

export type AdmonitionType = keyof typeof admonitionConfig;

interface AdmonitionProps {
  type?: AdmonitionType;
  title?: string;
  children: React.ReactNode;
  /** Override the default icon for this variant. */
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

export function Admonition({
  type = "note",
  title,
  children,
  icon: CustomIcon,
  className = "",
}: AdmonitionProps) {
  const config = admonitionConfig[type];
  const IconComponent = CustomIcon ?? config.icon;

  return (
    <div
      role="alert"
      data-admonition-type={type}
      className={`${config.bgColor} ${config.borderColor} border rounded-lg p-4 ${className}`.trim()}
    >
      <div className="flex gap-3">
        <div className={`${config.iconColor} flex-shrink-0 mt-0.5`}>
          <IconComponent className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          {title && (
            <div className={`${config.textColor} font-semibold text-sm mb-1`}>
              {title}
            </div>
          )}
          <div className={`${config.textColor} text-sm leading-relaxed`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admonition;
